import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Form } from "@/shared/ui/form";
import { Label } from "@/shared/ui/label";
import {
  ReceivableCurrency,
  ReceivableTaxType,
  ReceivableTaxDocument,
  PendingReceivableTax,
  PendingReceivableTaxDocument,
} from "../../utils/types";

import {
  taxFormSchema,
  TaxFormValues,
} from "./utils";
import { TaxFormFields } from "./TaxFormFields";
import { TaxDocumentForm } from "./TaxDocumentForm";
import { TaxDocumentList } from "./TaxDocumentList";
import { useTaxDocuments } from "./hooks/useTaxDocuments";
import { TaxListItem } from "./utils";

interface TaxEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tax: TaxListItem | null;
  existingDocuments?: ReceivableTaxDocument[];
  loadingDocuments?: boolean;
  onSubmit: (
    values: TaxFormValues,
    pendingDocuments: PendingReceivableTax["documents"]
  ) => Promise<void>;
  onDocumentAdd?: (document: { documentType: string; description?: string; file: File }) => Promise<void>;
  onDocumentRemove?: (document: ReceivableTaxDocument | PendingReceivableTaxDocument) => Promise<void>;
  disabled?: boolean;
  isDocumentMutationPending?: boolean;
}

export function TaxEditDialog({
  open,
  onOpenChange,
  tax,
  existingDocuments = [],
  loadingDocuments = false,
  onSubmit,
  onDocumentAdd,
  onDocumentRemove,
  disabled = false,
  isDocumentMutationPending = false,
}: TaxEditDialogProps) {
  const form = useForm<TaxFormValues>({
    resolver: zodResolver(taxFormSchema),
    defaultValues: {
      currency: ReceivableCurrency.BRL,
      type: ReceivableTaxType.IOF,
      description: "",
      document: "",
      value: 0,
    },
  });

  const documentsHook = useTaxDocuments();
  const isSubmitting = form.formState.isSubmitting;

  // Inicializar formulário quando tax mudar
  useEffect(() => {
    if (tax && open) {
      const base = tax.tax;
      const resolvedType = Object.values(ReceivableTaxType).includes(base.type as ReceivableTaxType)
        ? (base.type as ReceivableTaxType)
        : ReceivableTaxType.OTHER;
      
      form.reset({
        currency: base.currency,
        type: resolvedType,
        description: base.description ?? "",
        document: base.document ?? "",
        value: base.value,
      });

      // Se for pending, carregar documentos pendentes
      if (tax.kind === "pending" && base.documents) {
        // Resetar e preparar para novos documentos
        documentsHook.reset();
      } else {
        documentsHook.reset();
      }
    }
  }, [tax, open, form, documentsHook]);

  useEffect(() => {
    if (!open) {
      form.reset({
        currency: ReceivableCurrency.BRL,
        type: ReceivableTaxType.IOF,
        description: "",
        document: "",
        value: 0,
      });
      documentsHook.reset();
    }
  }, [open, form, documentsHook]);

  const handleDocumentAdd = async () => {
    if (!documentsHook.draft.file || !documentsHook.draft.documentType) return;
    
    if (onDocumentAdd && tax?.kind === "existing") {
      await onDocumentAdd({
        documentType: documentsHook.draft.documentType,
        description: documentsHook.draft.description || undefined,
        file: documentsHook.draft.file,
      });
      documentsHook.reset();
    } else {
      documentsHook.addDocument();
    }
  };

  const handleDocumentRemove = async (document: ReceivableTaxDocument | PendingReceivableTaxDocument) => {
    if (onDocumentRemove) {
      await onDocumentRemove(document);
    } else {
      documentsHook.removeDocument(document.id);
    }
  };

  const handleSubmit = async (values: TaxFormValues) => {
    if (disabled || isSubmitting || !tax) return;
    await onSubmit(values, documentsHook.documents);
  };

  const isBusy = disabled || isSubmitting || isDocumentMutationPending;
  const totalDocuments = existingDocuments.length + documentsHook.documents.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar taxa</DialogTitle>
          <DialogDescription>Ajuste as informações da taxa conforme necessário.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(handleSubmit)(e);
            }}
            className="space-y-4"
          >
            <TaxFormFields form={form} disabled={isBusy} />

            <div className="space-y-4 rounded-lg border border-white/10 bg-muted/15 p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Documentos da taxa (opcional)</h3>
                  <p className="text-xs text-muted-foreground">
                    Adicione novos anexos ou remova os já vinculados.
                  </p>
                </div>
                {totalDocuments > 0 && (
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {totalDocuments} documento{totalDocuments !== 1 ? "s" : ""}
                  </Label>
                )}
              </div>

              <TaxDocumentForm
                draft={documentsHook.draft}
                onDraftChange={documentsHook.updateDraft}
                onAdd={handleDocumentAdd}
                error={documentsHook.error}
                disabled={isBusy}
                documentsCount={totalDocuments}
                fileInputRef={documentsHook.fileInputRef}
                acceptedTypes={documentsHook.acceptedTypes}
                onFileChange={documentsHook.handleFileChange}
                onFileButtonClick={() => documentsHook.handleFileButtonClick(isBusy)}
                onFileRemove={documentsHook.handleFileRemove}
              />

              {documentsHook.documents.length > 0 && (
                <TaxDocumentList
                  documents={documentsHook.documents}
                  onRemove={handleDocumentRemove}
                  disabled={isBusy}
                  title="Documentos adicionados nesta edição"
                />
              )}

              {tax?.kind === "existing" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Documentos vinculados ({existingDocuments.length})
                    </Label>
                    {loadingDocuments && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  {existingDocuments.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum documento anexado.</p>
                  ) : (
                    <TaxDocumentList
                      documents={existingDocuments}
                      onRemove={handleDocumentRemove}
                      disabled={isBusy}
                    />
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenChange(false);
                }}
                disabled={isBusy}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isBusy || isSubmitting} onClick={(e) => e.stopPropagation()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar alterações"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

